import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {normalizeCheckoutProfileDraft, type CheckoutProfileMissing} from '@dms/api/sales';

import type {CheckoutProfileFormValues} from '../types/CheckoutProfileFormValuesSchema';
import {getCheckoutProfileMissingRequiredFields} from './getCheckoutProfileMissingRequiredFields';

type FieldCandidate = [isMissing: boolean, field: string | undefined];

const firstMissingField = (candidates: FieldCandidate[]) =>
  candidates.find(([isMissing, field]) => isMissing && isNotNil(field))?.[1];

/**
 * Names the form field that explains a locally detected "complete the required settings"
 * state of a tab, so the feedback "View" link can focus it instead of the first control.
 * Returns undefined when the tab has no single field to point at (Documents, or a
 * complete tab).
 */
export function getCheckoutProfileLocalIssueField(
  tab: CheckoutProfileMissing['tab'],
  profile: CheckoutProfileFormValues
) {
  const isInternal = profile.typeOfSale === 'INTERNAL';
  const missingRequiredField = getCheckoutProfileMissingRequiredFields(
    normalizeCheckoutProfileDraft(profile)
  )
    .find((path) => path[0] === tab.toLowerCase())
    ?.join('.');

  return match(tab)
    .with('GENERAL', () =>
      firstMissingField([
        [isNilOrEmpty(profile.name?.trim()), 'name'],
        [isNilOrEmpty(profile.branchId), 'branchId'],
        [isNil(profile.typeOfSale), 'typeOfSale'],
        [
          !isInternal &&
            profile.foreignCurrency?.isAllowed === true &&
            profile.foreignCurrency.isDefault === true &&
            isNilOrEmpty(profile.foreignCurrency.defaultCurrency),
          'foreignCurrency.defaultCurrency',
        ],
      ])
    )
    .with('PAYMENTS', () =>
      isInternal
        ? undefined
        : firstMissingField([
            [isNilOrEmpty(profile.payments?.methods), 'payments.methods'],
            [isNil(profile.payments?.defaultMethod), 'payments.defaultMethod'],
            [isNotNil(missingRequiredField), missingRequiredField],
          ])
    )
    .with('DEPOSIT', () =>
      isInternal || profile.deposit?.isAllowed !== true
        ? undefined
        : firstMissingField([
            [isNilOrEmpty(profile.deposit.methods), 'deposit.methods'],
            [isNil(profile.deposit.defaultMethod), 'deposit.defaultMethod'],
            [isNotNil(missingRequiredField), missingRequiredField],
          ])
    )
    .with('DOCUMENTS', () => undefined)
    .exhaustive();
}
